import { dayjsTimezone } from './date.utils';
import { FormGroup } from '@angular/forms';
import { Buyer, TeamMember } from '@tripointe/tripointe-cms/data-access';
import {
  Attendees,
  AttendeesToGroup,
  DropdownGroup,
  ParsedInvitee
} from '@tripointe/tripointe-cms/types';
import * as dayjs from 'dayjs';

export interface GenerateLotAppointmentInputVariables {
  form: FormGroup;
  timeZone: string;
  isCreate?: boolean;
}

export const getInviteeIdsWithPrefix = (invitee): string => {
  return `${invitee.__typename}_${invitee.id}`;
};

const buyerToOption = (buyer: Partial<Buyer>) => {
  return {
    text: `${buyer.fullName} (${buyer.role.label})`,
    value: getInviteeIdsWithPrefix(buyer)
  };
};

const teamMemberToOption = (teamMember: Partial<TeamMember>) => {
  return {
    text: teamMember.fullName,
    value: getInviteeIdsWithPrefix(teamMember)
  };
};

export const mapInviteesToDropdown = (invitees: Array<Buyer | TeamMember>) => {
  return invitees.map(invitee => getInviteeIdsWithPrefix(invitee));
};

export const buyersToOptionsGroup = (buyers: any): DropdownGroup => ({
  label: AttendeesToGroup[Attendees.buyer],
  options: buyers.map(buyerToOption)
});

export const teamMembersToOptionsGroup = (teamMembers: any): DropdownGroup => ({
  label: AttendeesToGroup[Attendees.teamMember],
  options: teamMembers.map(teamMemberToOption)
});

const parseInvitee = (invitee: string) => {
  const splitted = invitee.split('_');

  return {
    type: splitted[0],
    value: splitted[1]
  } as ParsedInvitee;
};

const toValue = (option: ParsedInvitee): number => {
  return Number(option.value);
};

export const mapToBuyersIds = (invitees: string[]): number[] => {
  return invitees
    .map(parseInvitee)
    .filter(invitee => invitee.type === Attendees.buyer)
    .map(toValue);
};

export const mapToTeamMembersIds = (invitees: string[]): number[] => {
  return invitees
    .map(parseInvitee)
    .filter(invitee => invitee.type === Attendees.teamMember)
    .map(toValue);
};

export const dateToFormat = (date: Date, isUTC: boolean = false): string => {
  return dayjs(date).utc(isUTC).format('YYYY-MM-DD');
};

export const generateLotAppointmentInput = (variables: GenerateLotAppointmentInputVariables) => {
  const { form, timeZone, isCreate } = variables;
  const {
    eventId,
    name,
    phaseId,
    description,
    date,
    start,
    end,
    invitee,
    rsvpRequired,
    address,
    address2,
    city,
    stateId,
    zip,
    requestedBy
  } = form.value;

  const commonData = {
    name,
    phaseId,
    description,
    start: dayjsTimezone(dateToFormat(date) + ' ' + start, timeZone).toISOString(),
    end: dayjsTimezone(dateToFormat(date) + ' ' + end, timeZone).toISOString(),
    buyersIds: mapToBuyersIds(invitee),
    teamMembersIds: mapToTeamMembersIds(invitee),
    rsvpRequired,
    address,
    address2,
    city,
    stateId,
    zip
  };

  return isCreate
    ? { ...commonData, eventId: typeof eventId === 'number' ? eventId : null, requestedBy }
    : { ...commonData };
};
